import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { createHevcDecoder } from "./hevcDecoder";
import { createMpegTsDemuxer } from "./mpegts";
import { drawYuvFrame } from "./drawYuv";
import { resolveHevcWebSocketUrl } from "./resolveWsUrl";
import "./EdgeH265Player.scss";

const enterFullScreenPath = "M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z";
const exitFullScreenPath = "M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z";
const MAX_RESUME_ATTEMPTS = 5;
const BURST_GAP_MS = 80;
const LIVE_HOLD_MS = 200;
const TAIL_BYTES = 256 * 1024;

function socketIsOpen(socket) {
  const state = socket?.readyState;
  return state === WebSocket.OPEN || state === WebSocket.CONNECTING;
}

const EdgeH265Player = ({ url, cameraName = "" }) => {
  const canvasRef = useRef(null);
  const shellRef = useRef(null);
  const playingRef = useRef(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [fullScreen, setFullScreen] = useState(false);

  useEffect(() => {
    const onChange = () => {
      setFullScreen(document.fullscreenElement === shellRef.current);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullScreen = () => {
    const element = shellRef.current;
    if (!element) return;
    if (document.fullscreenElement === element) {
      document.exitFullscreen();
      return;
    }
    if (element.requestFullscreen) element.requestFullscreen();
    else if (element.webkitRequestFullscreen) element.webkitRequestFullscreen();
    else if (element.msRequestFullscreen) element.msRequestFullscreen();
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !url) return undefined;

    let stopped = false;
    let socket = null;
    let decoder = null;
    let generation = 0;
    let starting = false;
    let resumeTimer = null;
    let resumeAttempts = 0;
    let wasHidden = false;
    let catchUp = false;
    let sawBurst = false;
    let tail = [];
    let tailBytes = 0;
    let settleTimer = null;
    let demuxer = null;
    let latestFrame = null;
    let drawLatestOnly = false;
    let pendingDraw = null;
    let lastDeliveredAt = 0;
    playingRef.current = false;
    setLoading(true);
    setError("");

    const markPlaying = () => {
      if (stopped) return;
      playingRef.current = true;
      resumeAttempts = 0;
      setLoading(false);
    };

    const closeSocket = () => {
      if (!socket) return;
      const previous = socket;
      socket = null;
      previous.onmessage = null;
      previous.onerror = null;
      previous.onclose = null;
      previous.close();
    };

    const pushTail = (chunk) => {
      tail.push(chunk);
      tailBytes += chunk.length;
      while (tail.length > 1 && tailBytes - tail[0].length >= TAIL_BYTES) {
        tailBytes -= tail[0].length;
        tail.shift();
      }
    };

    const clearSettleTimer = () => {
      if (!settleTimer) return;
      clearTimeout(settleTimer);
      settleTimer = null;
    };

    const paintPending = () => {
      if (pendingDraw && drawYuvFrame(canvas, pendingDraw)) {
        latestFrame = pendingDraw;
        markPlaying();
        return;
      }
      if (latestFrame) drawYuvFrame(canvas, latestFrame);
    };

    const finishCatchUp = () => {
      if (stopped || !catchUp || !tail.length || !demuxer) return;
      catchUp = false;
      sawBurst = false;
      clearSettleTimer();
      const chunks = tail;
      tail = [];
      tailBytes = 0;
      drawLatestOnly = true;
      pendingDraw = null;
      let jumped = false;
      try {
        jumped = demuxer.jumpToLive(chunks);
      } catch {
        jumped = false;
      }
      drawLatestOnly = false;
      paintPending();
      if (!jumped && demuxer.resync) demuxer.resync();
      lastDeliveredAt = performance.now();
    };

    const armLiveHold = () => {
      if (settleTimer) return;
      settleTimer = setTimeout(() => {
        settleTimer = null;
        if (stopped || document.visibilityState === "hidden" || !catchUp || sawBurst) return;
        finishCatchUp();
      }, LIVE_HOLD_MS);
    };

    const start = async () => {
      if (stopped || starting) return;
      const generationId = generation + 1;
      generation = generationId;
      starting = true;
      clearSettleTimer();
      catchUp = false;
      sawBurst = false;
      tail = [];
      tailBytes = 0;
      lastDeliveredAt = 0;
      closeSocket();
      if (decoder) {
        decoder.destroy();
        decoder = null;
      }
      try {
        const nextDecoder = await createHevcDecoder();
        if (stopped || generationId !== generation) {
          nextDecoder.destroy();
          return;
        }
        decoder = nextDecoder;
        demuxer = createMpegTsDemuxer((accessUnit) => {
          if (stopped || generationId !== generation || !decoder) return;
          try {
            const frames = decoder.feed(accessUnit);
            if (drawLatestOnly) {
              if (frames.length) pendingDraw = frames[frames.length - 1];
              return;
            }
            frames.forEach((frame) => {
              if (drawYuvFrame(canvas, frame)) {
                latestFrame = frame;
                markPlaying();
              }
            });
          } catch {
            if (!stopped && generationId === generation && !drawLatestOnly) {
              setLoading(false);
              setError("Unable to decode this H.265 stream.");
            }
          }
        });
        const nextSocket = new WebSocket(resolveHevcWebSocketUrl(url));
        socket = nextSocket;
        nextSocket.binaryType = "arraybuffer";
        nextSocket.onmessage = (event) => {
          if (stopped || socket !== nextSocket) return;
          const bytes = new Uint8Array(event.data);
          const now = performance.now();
          const gap = lastDeliveredAt ? now - lastDeliveredAt : Number.POSITIVE_INFINITY;
          lastDeliveredAt = now;
          if (document.visibilityState === "hidden") {
            catchUp = true;
            sawBurst = false;
            clearSettleTimer();
            return;
          }
          if (catchUp) {
            pushTail(bytes);
            if (gap < BURST_GAP_MS) {
              sawBurst = true;
              clearSettleTimer();
              return;
            }
            if (sawBurst) {
              finishCatchUp();
              return;
            }
            armLiveHold();
            return;
          }
          demuxer.push(bytes);
        };
        nextSocket.onerror = () => {
          if (!stopped && socket === nextSocket && !playingRef.current) {
            setLoading(false);
            setError("Unable to open the H.265 stream.");
          }
        };
        nextSocket.onclose = () => {
          if (stopped || socket !== nextSocket) return;
          socket = null;
          if (document.visibilityState === "hidden" || !playingRef.current) return;
          scheduleResume();
        };
      } catch {
        if (!stopped && generationId === generation) {
          setLoading(false);
          setError("Unable to start the H.265 player.");
        }
      } finally {
        if (generationId === generation) starting = false;
      }
    };

    const scheduleResume = () => {
      if (stopped || starting || resumeTimer) return;
      if (socketIsOpen(socket)) return;
      resumeAttempts += 1;
      if (resumeAttempts > MAX_RESUME_ATTEMPTS) {
        setLoading(false);
        setError("Unable to open the H.265 stream.");
        return;
      }
      setLoading(true);
      setError("");
      const delay = Math.min(500 * resumeAttempts, 4000);
      resumeTimer = setTimeout(() => {
        resumeTimer = null;
        if (stopped || starting || socketIsOpen(socket)) return;
        start();
      }, delay);
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        wasHidden = true;
        catchUp = true;
        sawBurst = false;
        clearSettleTimer();
        return;
      }
      if (!wasHidden || stopped) return;
      wasHidden = false;
      if (latestFrame) drawYuvFrame(canvas, latestFrame);
      if (!socketIsOpen(socket)) {
        if (resumeTimer) {
          clearTimeout(resumeTimer);
          resumeTimer = null;
        }
        resumeAttempts = 0;
        setLoading(true);
        setError("");
        start();
        return;
      }
      if (catchUp) armLiveHold();
    };

    document.addEventListener("visibilitychange", onVisibility);
    start();

    return () => {
      stopped = true;
      clearTimeout(resumeTimer);
      clearSettleTimer();
      document.removeEventListener("visibilitychange", onVisibility);
      closeSocket();
      decoder?.destroy();
    };
  }, [url]);

  return (
    <div className="videoPlaer_wrapper">
      <div
        ref={shellRef}
        className="edge-h265-stage"
        style={fullScreen ? { width: "100%", height: "100%" } : undefined}
      >
        <canvas
          ref={canvasRef}
          className="edge-h265-canvas"
          role="img"
          aria-label={`Live streaming from ${cameraName || "camera"}`}
        />
        {loading && !error && <div className="edge-h265-loader" role="status" aria-label="Loading video" />}
        {!loading && !error && (
          <div className="edge-h265-controls">
            <button
              type="button"
              className="edge-h265-fullscreen"
              onClick={toggleFullScreen}
              aria-label={fullScreen ? "Exit full screen" : "Full screen"}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="currentColor" d={fullScreen ? exitFullScreenPath : enterFullScreenPath} />
              </svg>
            </button>
          </div>
        )}
      </div>
      {error && (
        <p role="alert" className="camera_details">
          {error}
        </p>
      )}
    </div>
  );
};

EdgeH265Player.propTypes = {
  url: PropTypes.string.isRequired,
  cameraName: PropTypes.string,
};

export default EdgeH265Player;
